import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { requestAiLearningPlan } from '../../utils/aiLearningPlan';
import { LearningPlanGuideView } from './LearningPlanGuideView';
import './LearningPlanRequestModal.css';

type Props = {
  learningTitle: string;
  subject: string;
  grade: string;
  unit: string;
  achievement: string;
  siblingLearnings: string[];
  onClose: () => void;
  onApply: (memo: string) => void;
};

const LearningPlanRequestModal: React.FC<Props> = ({
  learningTitle,
  subject,
  grade,
  unit,
  achievement,
  siblingLearnings,
  onClose,
  onApply,
}) => {
  const [request, setRequest] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<string | null>(null);
  const aliveRef = useRef(true);

  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  const submit = () => {
    if (busy) return;
    setBusy(true);
    setError('');
    void requestAiLearningPlan({
      subject,
      grade,
      unit,
      learning: learningTitle,
      achievement,
      siblingLearnings,
      request,
    })
      .then((memo) => {
        if (!aliveRef.current) return;
        setResult(memo);
        setBusy(false);
      })
      .catch((e: unknown) => {
        if (!aliveRef.current) return;
        setError(e instanceof Error ? e.message : '학습계획을 만들지 못했습니다.');
        setBusy(false);
      });
  };

  return createPortal(
    <div className="cp-learning-plan-request" role="presentation" onMouseDown={(e) => e.stopPropagation()}>
      <button type="button" className="cp-learning-plan-request-backdrop" aria-label="닫기" onClick={busy ? undefined : onClose} />
      <div
        className={result != null ? 'cp-learning-plan-request-card is-result' : 'cp-learning-plan-request-card'}
        role="dialog"
        aria-labelledby="cp-learning-plan-request-title"
      >
        {result != null ? (
          <>
            <h3 id="cp-learning-plan-request-title">학습계획 결과</h3>
            <p className="cp-learning-plan-request-lead">{learningTitle.trim() ? learningTitle.trim() : '학습내용'}</p>
            <p className="cp-learning-plan-request-hint">내용을 고친 뒤 반영하면 학습 메모에 들어갑니다.</p>
            <LearningPlanGuideView
              guide={result}
              editable
              ariaLabel="학습계획 결과"
              className="cp-learning-plan-result-guide"
              onChange={setResult}
            />
            <div className="cp-learning-plan-request-actions">
              <button type="button" onClick={onClose}>
                닫기
              </button>
              <button
                type="button"
                className="is-apply"
                onClick={() => {
                  onApply(result);
                  onClose();
                }}
              >
                반영
              </button>
            </div>
          </>
        ) : (
          <>
        <h3 id="cp-learning-plan-request-title">학습계획 요청</h3>
        <p className="cp-learning-plan-request-lead">{learningTitle.trim() ? learningTitle.trim() : '학습내용'}</p>
        {unit.trim() ? <p className="cp-learning-plan-request-meta">단원 {unit.trim()}</p> : null}
        {achievement.trim() ? <p className="cp-learning-plan-request-meta">성취기준 {achievement.trim()}</p> : null}
        <label>
          <span>대략적인 계획이나 요구</span>
          <textarea
            rows={5}
            value={request}
            autoFocus
            disabled={busy}
            placeholder="예: 호기심 가는 이야기를 하고, 모둠으로 특징을 조사한 뒤 한 문장으로 정리"
            onChange={(e) => setRequest(e.target.value)}
          />
        </label>
        <p className="cp-learning-plan-request-hint">
          이 차시의 단원, 학습내용, 성취기준을 보고 도입·배움·정리로 나눕니다. 활동은 ~하기로 쓰고, 확인기준 다음에는 오개념과 피드백을 적습니다.
        </p>
        {error ? <p className="cp-learning-plan-request-error">{error}</p> : null}
        <div className="cp-learning-plan-request-actions">
          <button type="button" onClick={onClose} disabled={busy}>
            취소
          </button>
          <button type="button" className="is-primary" disabled={busy} onClick={submit}>
            {busy ? '만드는 중…' : 'AI 학습계획 생성'}
          </button>
        </div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
};

export default LearningPlanRequestModal;
